import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../config/axios';
import ProduitCard from '../components/ProduitCard';
import ArtisanCard from '../components/ArtisanCard';
import TraditionalPattern from '../components/TraditionalPattern';
import SEO from '../components/SEO';
import { useTheme } from '../context/ThemeContext';

const Accueil = () => {
  const { darkMode } = useTheme();
  const [produitsFeatured, setProduitsFeatured] = useState([]);
  const [artisansFeatured, setArtisansFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalArtisansGlobal, setTotalArtisansGlobal] = useState(0);
  const [totalProduitsGlobal, setTotalProduitsGlobal] = useState(0);
  
  // Refs pour les animations
  const heroRef = useRef(null);
  const produitsRef = useRef(null);
  const artisansRef = useRef(null);
  const ctaRef = useRef(null);
  const categoriesRef = useRef(null);
  const statsRef = useRef(null);
  
  // Infinite scroll states
  const [currentPageProduits, setCurrentPageProduits] = useState(1);
  const [currentPageArtisans, setCurrentPageArtisans] = useState(1);
  const [hasMoreProduits, setHasMoreProduits] = useState(true);
  const [hasMoreArtisans, setHasMoreArtisans] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Items per page - Uniformisé à 8 produits pour tous les appareils
  const itemsPerPage = 8; // 8 produits par chargement

  // Animation d'apparition au scroll
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Animation spéciale pour la section CTA
          if (entry.target === ctaRef.current) {
            entry.target.classList.add('animate-scale-in-bounce');
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'scale(1)';
          } else {
            entry.target.classList.add('animate-fade-in-up');
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
          }
          
          // Animer les enfants avec un délai progressif
          const children = entry.target.querySelectorAll('.animate-on-scroll');
          children.forEach((child, index) => {
            setTimeout(() => {
              child.style.opacity = '1';
              child.style.transform = entry.target === ctaRef.current ? 'scale(1)' : 'translateY(0)';
            }, index * 150);
          });
        }
      });
    }, observerOptions);

    const sections = [
      heroRef.current,
      produitsRef.current,
      artisansRef.current,
      ctaRef.current,
      categoriesRef.current,
      statsRef.current
    ];

    sections.forEach(section => {
      if (section) {
        // Animation spéciale pour la section CTA
        if (section === ctaRef.current) {
          section.style.opacity = '0';
          section.style.transform = 'scale(0.8)';
          section.style.transition = 'opacity 1s ease-out, transform 1s cubic-bezier(0.34, 1.56, 0.64, 1)';
        } else {
          section.style.opacity = '0';
          section.style.transform = 'translateY(30px)';
          section.style.transition = 'opacity 0.8s ease-out, transform 0.8s ease-out';
        }
        observer.observe(section);
        
        // Préparer les enfants pour l'animation
        const children = section.querySelectorAll('.animate-on-scroll');
        children.forEach(child => {
          child.style.opacity = '0';
          if (section === ctaRef.current) {
            child.style.transform = 'scale(0.9)';
            child.style.transition = 'opacity 0.8s ease-out, transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)';
          } else {
            child.style.transform = 'translateY(20px)';
            child.style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out';
          }
        });
      }
    });

    return () => {
      sections.forEach(section => {
        if (section) observer.unobserve(section);
      });
    };
  }, []);

  // Auto-scroll pour la section impact - Désactivé car section cachée sur mobile

  const categories = [
    { nom: 'Textile et Tissage', icon: 'fas fa-tshirt', image: '/images/textile.jpg', color: 'from-purple-500 to-pink-500' },
    { nom: 'Poterie et Céramique', icon: 'fas fa-jar', image: '/images/poterie.jpg', color: 'from-orange-500 to-red-500' },
    { nom: 'Sculpture sur Bois', icon: 'fas fa-hammer', image: '/images/sculpture.jpg', color: 'from-amber-600 to-yellow-600' },
    { nom: 'Maroquinerie', icon: 'fas fa-briefcase', image: '/images/maroquinerie.jpg', color: 'from-amber-700 to-orange-700' },
    { nom: 'Bijouterie Traditionnelle', icon: 'fas fa-gem', image: '/images/bijouterie.jpg', color: 'from-yellow-400 to-amber-500' },
    { nom: 'Vannerie', icon: 'fas fa-basket-shopping', image: '/images/vannerie.jpg', color: 'from-green-600 to-teal-600' },
  ];

  // Chargement initial des données
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Sur desktop, charger seulement 8 produits. Sur mobile, charger avec scroll infini
        const isMobile = window.innerWidth < 768;
        const limit = isMobile ? itemsPerPage : 8;
        
        const [produitsRes, artisansRes] = await Promise.all([
          axiosInstance.get(`/produits?page=1&limit=${limit}&sortBy=vues&order=desc`),
          axiosInstance.get(`/artisans?page=1&limit=${itemsPerPage}`)
        ]);

        setProduitsFeatured(produitsRes.data.produits || []);
        setArtisansFeatured(artisansRes.data.artisans || []);
        
        // Vérifier s'il y a plus de produits/artisans
        // Sur desktop, on ne charge plus après les 8 premiers
        // Sur mobile, on continue avec le scroll infini
        const totalProduits = produitsRes.data.totalProduits || 0;
        const produitsCharges = produitsRes.data.produits?.length || 0;
        const hasMore = isMobile ? (totalProduits > produitsCharges) : false;
        console.log('Initial load - Total produits:', totalProduits, 'Chargés:', produitsCharges, 'Has more:', hasMore, 'Is mobile:', isMobile);
        setHasMoreProduits(hasMore);
        
        const totalArtisans = artisansRes.data.totalArtisans || 0;
        const artisansCharges = artisansRes.data.artisans?.length || 0;
        setHasMoreArtisans(totalArtisans > artisansCharges);
        
        // Mettre à jour les totaux globaux pour la section Impact (une seule fois)
        if (!totalProduitsGlobal && !totalArtisansGlobal) {
          setTotalProduitsGlobal(produitsRes.data.totalProduits || produitsRes.data.produits?.length || 0);
          setTotalArtisansGlobal(artisansRes.data.totalArtisans || artisansRes.data.artisans?.length || 0);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Chargement de plus de produits (scroll infini)
  useEffect(() => {
    if (currentPageProduits > 1) {
      const loadMoreProduits = async () => {
        setLoadingMore(true);
        try {
          const produitsRes = await axiosInstance.get(`/produits?page=${currentPageProduits}&limit=${itemsPerPage}&sortBy=vues&order=desc`);
          const newProduits = produitsRes.data.produits || [];
          
          if (newProduits.length > 0) {
            setProduitsFeatured(prev => {
              const newList = [...prev, ...newProduits];
              // Vérifier s'il reste des produits à charger
              const totalProduits = produitsRes.data.totalProduits || 0;
              const hasMore = newList.length < totalProduits;
              console.log('Load more - Total produits:', totalProduits, 'Chargés:', newList.length, 'Has more:', hasMore, 'Page:', currentPageProduits);
              setHasMoreProduits(hasMore);
              return newList;
            });
          } else {
            console.log('No more produits to load');
            setHasMoreProduits(false);
          }
        } catch (error) {
          console.error('Erreur lors du chargement de plus de produits:', error);
          setHasMoreProduits(false);
        } finally {
          setLoadingMore(false);
        }
      };

      loadMoreProduits();
    }
  }, [currentPageProduits, itemsPerPage]);

  // Chargement de plus d'artisans (scroll infini)
  useEffect(() => {
    if (currentPageArtisans > 1) {
      const loadMoreArtisans = async () => {
        setLoadingMore(true);
        try {
          const artisansRes = await axiosInstance.get(`/artisans?page=${currentPageArtisans}&limit=${itemsPerPage}`);
          const newArtisans = artisansRes.data.artisans || [];
          
          if (newArtisans.length > 0) {
            setArtisansFeatured(prev => [...prev, ...newArtisans]);
            const totalPages = Math.ceil((artisansRes.data.totalArtisans || 0) / itemsPerPage);
            setHasMoreArtisans(currentPageArtisans < totalPages);
          } else {
            setHasMoreArtisans(false);
          }
        } catch (error) {
          console.error('Erreur lors du chargement de plus d\'artisans:', error);
          setHasMoreArtisans(false);
        } finally {
          setLoadingMore(false);
        }
      };

      loadMoreArtisans();
    }
  }, [currentPageArtisans, itemsPerPage]);

  // Observer pour le scroll infini des produits (mobile uniquement)
  const produitsObserverRef = useRef(null);
  const produitsLoadMoreRef = useRef(null);

  useEffect(() => {
    // Ne créer l'observer que sur mobile et si on a encore des produits à charger
    const isMobile = window.innerWidth < 768; // md breakpoint
    
    if (!hasMoreProduits || !isMobile) {
      if (produitsObserverRef.current) {
        produitsObserverRef.current.disconnect();
        produitsObserverRef.current = null;
      }
      return;
    }

    // Nettoyer l'ancien observer
    if (produitsObserverRef.current) {
      produitsObserverRef.current.disconnect();
    }

    // Créer l'observer avec une référence stable
    produitsObserverRef.current = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        // Utiliser une fonction pour vérifier l'état actuel
        if (entry.isIntersecting) {
          setCurrentPageProduits(prev => {
            // Vérifier les conditions avant d'incrémenter
            if (hasMoreProduits && !loadingMore && window.innerWidth < 768) {
              console.log('Triggering load more produits - Page:', prev + 1);
              return prev + 1;
            }
            return prev;
          });
        }
      },
      { 
        threshold: 0.1, 
        rootMargin: '300px' // Augmenté pour déclencher plus tôt sur mobile
      }
    );

    // Observer l'élément quand il est disponible
    const currentRef = produitsLoadMoreRef.current;
    if (currentRef && produitsObserverRef.current) {
      console.log('Observing produitsLoadMoreRef element');
      produitsObserverRef.current.observe(currentRef);
    }

    return () => {
      if (produitsObserverRef.current) {
        produitsObserverRef.current.disconnect();
        produitsObserverRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMoreProduits, loadingMore]);

  // Réobserver quand le DOM change (après chargement de nouveaux produits) - Mobile uniquement
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (produitsLoadMoreRef.current && produitsObserverRef.current && hasMoreProduits && !loadingMore && isMobile) {
      // Réobserver après que le DOM soit mis à jour
      setTimeout(() => {
        if (produitsLoadMoreRef.current && produitsObserverRef.current) {
          produitsObserverRef.current.observe(produitsLoadMoreRef.current);
        }
      }, 100);
    }
  }, [produitsFeatured.length, hasMoreProduits, loadingMore]);

  // Observer pour le scroll infini des artisans
  const artisansObserverRef = useRef(null);
  const artisansLoadMoreRef = useRef(null);

  useEffect(() => {
    if (artisansObserverRef.current) {
      artisansObserverRef.current.disconnect();
    }

    artisansObserverRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreArtisans && !loadingMore) {
          setCurrentPageArtisans(prev => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (artisansLoadMoreRef.current) {
      artisansObserverRef.current.observe(artisansLoadMoreRef.current);
    }

    return () => {
      if (artisansObserverRef.current) {
        artisansObserverRef.current.disconnect();
      }
    };
  }, [hasMoreArtisans, loadingMore]);

  // Image du monument selon le thème
  const monumentImage = darkMode ? '/images/monument_dark.png' : '/images/monument.png';

  return (
    <div className="min-h-screen dark:bg-gray-900">
      <SEO 
        title="Village Artisanal Sénégal | Artisanat FIDAK Soumbédioune Dakar | Ministère Artisanat"
        description="Village Artisanal de Soumbédioune - Premier marché d'artisanat sénégalais authentique à Dakar. Partenaire officiel FIDAK, soutenu par le Ministère de l'Artisanat du Sénégal. Découvrez poterie, vannerie, bijoux traditionnels, maroquinerie, textiles bogolan et batik made in Senegal. Commerce équitable, artisans certifiés."
        keywords="village artisanal, village artisanal sénégal, village artisanal soumbédioune, artisanat sénégal, artisans sénégalais, FIDAK, FIDAK sénégal, soumbédioune, soumbédioune dakar, ministère artisanat sénégal, art sénégalais, artisanat dakar, poterie sénégal, vannerie traditionnelle, bijoux africains, bijoux sénégalais, maroquinerie cuir dakar, textile bogolan, batik sénégal, made in senegal, commerce équitable, marché artisanal dakar"
        type="website"
      />
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden dark:bg-gray-900">
     

        {/* Background Pattern */}
        <div className="absolute inset-0">
          <TraditionalPattern variant="circles" opacity={0.08} className="text-primary-800 dark:text-primary-400" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/10 via-transparent to-accent-500/10 dark:from-primary-800/20 dark:via-transparent dark:to-accent-400/20"></div>
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-accent-300/20 dark:bg-accent-400/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary-300/20 dark:bg-primary-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
           
            <h1 className="animate-on-scroll font-display text-4xl md:text-6xl font-bold text-primary-900 dark:text-primary-300 leading-tight">
              Découvrez l'Art <span className="bg-gradient-to-r from-accent-500 to-accent-600 bg-clip-text text-transparent relative">
                Traditionnel
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-accent-500 to-accent-600 transform scale-x-0 animate-expand-underline"></div>
              </span> du Sénégal
            </h1>
            <p className="animate-on-scroll text-xl text-gray-700 dark:text-gray-300 leading-relaxed max-w-lg">
               Collection unique de produits artisanaux authentiques, 
              créés par des artisans sénégalais.
            </p>
            <div className="animate-on-scroll flex flex-col sm:flex-row gap-4">
              <Link 
                to="/produits" 
                className="bg-gradient-to-r from-primary-800 to-primary-900 dark:from-primary-700 dark:to-primary-800 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 text-center"
              >
                Découvrir les Produits
              </Link>
              <Link 
                to="/artisans" 
                className="border-2 border-primary-800 dark:border-primary-600 text-primary-800 dark:text-primary-400 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-primary-800 dark:hover:bg-primary-700 hover:text-white hover:shadow-2xl hover:scale-105 transition-all duration-300 text-center"
              >
                Rencontrer les Artisans
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="animate-on-scroll relative">
              <img 
                src={monumentImage} 
                alt="Artisan sénégalais" 
                className="w-full rounded-2xl shadow-2xl hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-900/20 dark:from-gray-900/40 to-transparent rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

    

      {/* Featured Products - Scroll Infini */}
      <section ref={produitsRef} id="produits-section" className="py-6 md:py-10 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4">
          {/* En-tête - Caché sur mobile */}
          <div className="hidden md:block animate-on-scroll flex items-center justify-between mb-10">
            <div>
              <h2 className="text-3xl font-display font-bold text-primary-900 dark:text-primary-300 mb-2">
                <span className="md:inline hidden">Produits Populaires</span>
                <span className="md:hidden">Produits</span>
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                <span className="md:inline hidden">Découvrez nos produits les plus appréciés</span>
              </p>
            </div>
            <Link to="/produits" className="flex items-center text-primary-800 dark:text-primary-400 hover:text-accent-600 dark:hover:text-accent-400 font-semibold transition-colors group">
              Voir tous les produits <i className="fas fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform"></i>
            </Link>
          </div>
          
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="relative w-16 h-16 mb-4">
                <div className="absolute inset-0 border-4 border-primary-200 dark:border-gray-700 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-transparent border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin"></div>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-lg">Chargement des produits...</p>
            </div>
          ) : (
            <>
              {/* Affichage desktop - Limité à 8 produits */}
              <div className="hidden md:grid md:grid-cols-4 gap-4 lg:gap-6">
                {produitsFeatured.slice(0, 8).map((produit) => (
                  <ProduitCard key={produit._id} produit={produit} showViews={false} />
                ))}
              </div>

              {/* Affichage mobile - Scroll infini complet */}
              <div className="md:hidden">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                {produitsFeatured.map((produit) => (
                  <ProduitCard key={produit._id} produit={produit} showViews={false} />
                ))}
              </div>

                {/* Scroll infini - Trigger pour charger plus avec loader amélioré style Alibaba - Mobile uniquement */}
                {hasMoreProduits && (
                  <div 
                    ref={produitsLoadMoreRef} 
                    className="flex items-center justify-center py-8 min-h-[200px] w-full"
                    style={{ minHeight: '200px' }}
                  >
                    {loadingMore ? (
                      <div className="flex flex-col items-center gap-4 w-full">
                        <div className="relative w-16 h-16">
                          <div className="absolute inset-0 border-4 border-primary-200 dark:border-gray-700 rounded-full"></div>
                          <div className="absolute inset-0 border-4 border-transparent border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin"></div>
                        </div>
                        <div className="flex flex-col items-center gap-2">
                          <span className="text-base font-semibold text-gray-700 dark:text-gray-300">Chargement de plus de produits</span>
                          <div className="flex items-center gap-2">
                            <div className="flex gap-1.5">
                              <div className="w-2 h-2 bg-primary-600 dark:bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                              <div className="w-2 h-2 bg-primary-600 dark:bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                              <div className="w-2 h-2 bg-primary-600 dark:bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="h-32 flex items-center justify-center w-full">
                        <div className="w-12 h-12 border-3 border-primary-300 dark:border-gray-600 rounded-full opacity-50"></div>
                      </div>
                    )}
                </div>
              )}
              </div>

              {/* Bouton "Voir tous les produits" sur desktop - Toujours affiché */}
              <div className="hidden md:flex justify-center mt-8">
                <Link 
                  to="/produits" 
                  className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors shadow-md hover:shadow-lg"
                >
                  Voir tous les produits
                  <i className="fas fa-arrow-right"></i>
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Featured Artisans - Caché sur mobile */}
      <section ref={artisansRef} id="artisans-section" className="hidden md:block py-10 bg-gradient-to-br from-primary-50 to-accent-50 dark:from-gray-800 dark:to-gray-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="animate-on-scroll flex items-center justify-between mb-10">
            <div>
              <h2 className="text-3xl font-display font-bold text-primary-900 dark:text-primary-300 mb-2">Nos Artisans</h2>
              <p className="text-gray-600 dark:text-gray-400">
                Rencontrez les maîtres artisans qui perpétuent les traditions
              </p>
            </div>
            <Link to="/artisans" className="hidden sm:flex items-center text-primary-800 dark:text-primary-400 hover:text-accent-600 dark:hover:text-accent-400 font-semibold transition-colors group">
              Voir tous les artisans <i className="fas fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform"></i>
            </Link>
          </div>
          
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <i className="fas fa-spinner fa-spin text-5xl text-accent-500 mb-4"></i>
              <p className="text-gray-600 dark:text-gray-400 text-lg">Chargement des artisans...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 lg:gap-6">
                {artisansFeatured.map((artisan) => (
                  <ArtisanCard key={artisan._id} artisan={artisan} />
                ))}
              </div>

              {/* Scroll infini - Trigger pour charger plus avec loader amélioré */}
              {hasMoreArtisans && (
                <div ref={artisansLoadMoreRef} className="flex items-center justify-center py-6 md:py-8">
                  {loadingMore && (
                    <div className="flex flex-col items-center gap-3 w-full">
                      <div className="relative w-12 h-12">
                        <div className="absolute inset-0 border-4 border-primary-200 dark:border-gray-700 rounded-full"></div>
                        <div className="absolute inset-0 border-4 border-transparent border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin"></div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Chargement</span>
                        <div className="flex gap-1">
                          <div className="w-1.5 h-1.5 bg-primary-600 dark:bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-1.5 h-1.5 bg-primary-600 dark:bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                          <div className="w-1.5 h-1.5 bg-primary-600 dark:bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          
          <div className="text-center mt-8 sm:hidden">
            <Link to="/artisans" className="inline-flex items-center text-primary-800 hover:text-accent-600 font-semibold transition-colors">
              Voir tous les artisans <i className="fas fa-arrow-right ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section - Caché sur mobile */}
      <section ref={ctaRef} className="hidden md:block py-24 bg-gradient-to-r from-primary-800 to-primary-900 dark:from-primary-900 dark:to-gray-900 relative overflow-hidden">
        <TraditionalPattern variant="geometric" opacity={0.15} className="text-accent-400 dark:text-accent-300" />
        <div className="absolute inset-0 bg-gradient-to-r from-accent-500/10 to-transparent dark:from-accent-100/5 dark:to-transparent"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-accent-400/20 dark:bg-accent-500/10 rounded-full blur-3xl"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h2 className="animate-on-scroll text-4xl font-display font-bold text-white mb-4">Vous êtes artisan ?</h2>
          <p className="animate-on-scroll text-xl text-yellow-100 dark:text-yellow-200 mb-8 max-w-2xl mx-auto">
            Rejoignez notre communauté et faites découvrir vos créations au monde entier
          </p>
          <Link to="/register" className="animate-on-scroll inline-block bg-accent-500 hover:bg-accent-600 dark:bg-accent-600 dark:hover:bg-accent-700 text-white font-semibold px-10 py-4 rounded-full text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all">
            Rejoindre Village Artisanal
          </Link>
        </div>
      </section>


 {/* Categories Section - Caché sur mobile */}
      <section ref={categoriesRef} className="hidden md:block py-10 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 relative overflow-hidden">
        {/* Fond décoratif */}
        <div className="absolute inset-0 opacity-5 dark:opacity-10">
          <TraditionalPattern variant="circles" opacity={0.2} className="text-primary-800 dark:text-primary-400" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4">
          {/* En-tête de section amélioré */}
          <div className="animate-on-scroll text-center mb-12">
            <div className="inline-flex items-center justify-center mb-4">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-accent-500"></div>
              <span className="mx-4 text-accent-600 font-semibold text-sm tracking-wider uppercase">Découvrez</span>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-accent-500"></div>
            </div>
            <h2 className="text-4xl md:text-5xl font-display font-bold text-primary-900 mb-4 relative inline-block">
              Nos Catégories
              <div className="absolute -bottom-2 left-1/4 right-1/4 h-1 bg-gradient-to-r from-accent-400 via-accent-600 to-accent-400 rounded-full"></div>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto mt-6">
              Explorez nos différentes catégories d'artisanat traditionnel sénégalais
            </p>
          </div>

          {/* Grille de catégories redessinée */}
          {/* Version scrollable sur mobile */}
          <div className="md:hidden overflow-x-auto scrollbar-hide -mx-4 px-4">
            <div className="flex gap-3 pb-4 min-w-max">
              {categories.map((categorie, index) => (
                <Link
                  key={index}
                  to={`/produits?categorie=${encodeURIComponent(categorie.nom)}`}
                  className="group relative block w-36 flex-shrink-0"
                >
                  {/* Carte compacte */}
                  <div className="relative overflow-hidden rounded-xl bg-white shadow-md hover:shadow-lg transition-all duration-300">
                    {/* Image de fond */}
                    <div className="relative h-28 overflow-hidden">
                      <img 
                        src={categorie.image} 
                        alt={categorie.nom} 
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                      {/* Fallback avec dégradé */}
                      <div 
                        className={`hidden absolute inset-0 bg-gradient-to-br ${categorie.color} items-center justify-center`}
                        style={{ display: 'none' }}
                      >
                        <i className={`${categorie.icon} text-white text-3xl opacity-30`}></i>
                      </div>
                      
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                      
                      {/* Icône */}
                      <div className="absolute top-2 right-2">
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${categorie.color} flex items-center justify-center shadow-lg`}>
                          <i className={`${categorie.icon} text-white text-xs`}></i>
                        </div>
                      </div>
                    </div>
                    
                    {/* Contenu texte */}
                    <div className="p-2 bg-white">
                      <h3 className="text-center font-display font-bold text-primary-900 text-xs leading-tight line-clamp-2">
                        {categorie.nom}
                      </h3>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Version grille pour desktop */}
          <div className="hidden md:grid grid-cols-3 lg:grid-cols-6 gap-6">
            {categories.map((categorie, index) => (
              <Link
                key={index}
                to={`/produits?categorie=${encodeURIComponent(categorie.nom)}`}
                className="group relative block"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Carte avec effet de profondeur */}
                <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 hover:scale-105">
                  {/* Image de fond avec overlay dynamique */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={categorie.image} 
                      alt={categorie.nom} 
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                    {/* Fallback avec dégradé si l'image n'existe pas */}
                    <div 
                      className={`hidden absolute inset-0 bg-gradient-to-br ${categorie.color} items-center justify-center`}
                      style={{ display: 'none' }}
                    >
                      <i className={`${categorie.icon} text-white text-5xl opacity-30`}></i>
                    </div>
                    
                    {/* Overlay avec dégradé */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent group-hover:from-black/80 transition-all duration-500"></div>
                    
                    {/* Icône flottante */}
                    <div className="absolute top-4 right-4 z-10">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${categorie.color} backdrop-blur-sm flex items-center justify-center shadow-xl transform group-hover:rotate-12 group-hover:scale-110 transition-all duration-300`}>
                        <i className={`${categorie.icon} text-white text-xl`}></i>
                      </div>
                    </div>
                    
                    {/* Badge avec compteur (optionnel) */}
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-primary-800 shadow-md">
                      #{index + 1}
                    </div>
                  </div>
                  
                  {/* Contenu texte */}
                  <div className="p-4 bg-white">
                    <h3 className="text-center font-display font-bold text-primary-900 group-hover:text-accent-600 transition-colors duration-300 text-sm leading-tight">
                      {categorie.nom}
                    </h3>
                    
                    {/* Barre de progression décorative */}
                    <div className="mt-3 h-1 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full bg-gradient-to-r ${categorie.color} transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500`}
                      ></div>
                    </div>
                  </div>
                  
                  {/* Effet de brillance au survol */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000 pointer-events-none"></div>
                </div>
              </Link>
            ))}
          </div>
          
          {/* Bouton "Voir plus" centré */}
          <div className="text-center mt-12">
            <Link 
              to="/produits" 
              className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-800 to-primary-900 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 group"
            >
              Découvrir toutes les catégories
              <i className="fas fa-arrow-right group-hover:translate-x-2 transition-transform duration-300"></i>
            </Link>
          </div>
        </div>
      </section>


        {/* Stats Section - Caché sur mobile */}
      <section ref={statsRef} className="hidden md:block py-12 bg-gradient-to-br from-amber-50 via-white to-primary-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 relative overflow-hidden">
        {/* Motifs décoratifs en arrière-plan */}
        <div className="absolute inset-0 opacity-5 dark:opacity-10">
          <TraditionalPattern variant="geometric" opacity={0.3} className="text-primary-900 dark:text-primary-400" />
        </div>
        
        {/* Éléments décoratifs flottants */}
        <div className="absolute top-10 left-10 w-40 h-40 bg-accent-200/30 dark:bg-accent-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-56 h-56 bg-primary-200/30 dark:bg-primary-400/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        
        {/* Titre de section artistique */}
        <div className="animate-on-scroll relative z-10 text-center mb-16">
          <div className="inline-block">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-primary-900 dark:text-primary-300 mb-3 relative">
              Notre Impact
              <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-accent-500 dark:via-accent-400 to-transparent"></div>
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-lg mt-4">Des chiffres qui racontent notre histoire</p>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4">
          {/* Container scrollable sur mobile avec auto-scroll */}
          <div 
            className="md:hidden overflow-x-auto scrollbar-hide -mx-4 px-4"
          >
            <div className="flex gap-3 pb-4 min-w-max">
              {/* Card Artisans */}
              <div className="relative group w-48 flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl transform rotate-2 group-hover:rotate-4 transition-transform duration-300 opacity-20 dark:opacity-30"></div>
                <div className="relative text-center p-5 bg-white/80 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl border-2 border-primary-200 dark:border-primary-700 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="relative mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-700 dark:from-primary-600 dark:to-primary-800 rounded-xl flex items-center justify-center mx-auto shadow-lg">
                      <i className="fas fa-users text-white text-xl"></i>
                    </div>
                  </div>
                  <h3 className="font-display text-3xl font-bold bg-gradient-to-br from-primary-700 to-primary-900 dark:from-primary-400 dark:to-primary-600 bg-clip-text text-transparent mb-2">
                    {loading ? '…' : totalArtisansGlobal}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 font-semibold text-sm">Artisans</p>
                </div>
              </div>

              {/* Card Produits */}
              <div className="relative group w-48 flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-br from-accent-400 to-accent-600 rounded-2xl transform -rotate-2 group-hover:-rotate-4 transition-transform duration-300 opacity-20 dark:opacity-30"></div>
                <div className="relative text-center p-5 bg-white/80 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl border-2 border-accent-200 dark:border-accent-700 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="relative mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-accent-500 to-accent-700 dark:from-accent-600 dark:to-accent-800 rounded-xl flex items-center justify-center mx-auto shadow-lg">
                      <i className="fas fa-box text-white text-xl"></i>
                    </div>
                  </div>
                  <h3 className="font-display text-3xl font-bold bg-gradient-to-br from-accent-700 to-accent-900 dark:from-accent-400 dark:to-accent-600 bg-clip-text text-transparent mb-2">
                    {loading ? '…' : totalProduitsGlobal}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 font-semibold text-sm">Produits</p>
                </div>
              </div>

              {/* Card Régions */}
              <div className="relative group w-48 flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-2xl transform rotate-1 group-hover:rotate-3 transition-transform duration-300 opacity-20 dark:opacity-30"></div>
                <div className="relative text-center p-5 bg-white/80 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl border-2 border-secondary-200 dark:border-secondary-700 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="relative mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-secondary-500 to-secondary-700 dark:from-secondary-600 dark:to-secondary-800 rounded-xl flex items-center justify-center mx-auto shadow-lg">
                      <i className="fas fa-map-marker-alt text-white text-xl"></i>
                    </div>
                  </div>
                  <h3 className="font-display text-3xl font-bold bg-gradient-to-br from-secondary-700 to-secondary-900 dark:from-red-400 dark:to-red-600 bg-clip-text text-transparent mb-2">
                    14
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 font-semibold text-sm">Régions</p>
                </div>
              </div>

              {/* Card Clients */}
              <div className="relative group w-48 flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-br from-red-400 to-red-600 dark:from-red-600 dark:to-red-800 rounded-2xl transform -rotate-1 group-hover:-rotate-3 transition-transform duration-300 opacity-20"></div>
                <div className="relative text-center p-5 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border-2 border-red-200 dark:border-red-700 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="relative mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-700 dark:from-red-600 dark:to-red-800 rounded-xl flex items-center justify-center mx-auto shadow-lg">
                      <i className="fas fa-heart text-white text-xl"></i>
                    </div>
                  </div>
                  <h3 className="font-display text-3xl font-bold bg-gradient-to-br from-red-700 to-red-900 dark:from-red-400 dark:to-red-600 bg-clip-text text-transparent mb-2">
                    750+
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 font-semibold text-sm">Clients</p>
                </div>
              </div>
            </div>
          </div>

          {/* Grid normal pour desktop */}
          <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Card Artisans */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-primary-400 to-primary-600 rounded-3xl transform rotate-3 group-hover:rotate-6 transition-transform duration-300 opacity-20"></div>
              <div className="relative text-center p-5 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl border-2 border-primary-200 shadow-lg hover:shadow-2xl hover:-translate-y-3 transition-all duration-300">
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl flex items-center justify-center mx-auto transform group-hover:rotate-12 group-hover:scale-110 transition-all duration-300 shadow-xl">
                    <i className="fas fa-users text-white text-3xl"></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-accent-400 rounded-full animate-ping opacity-30"></div>
                </div>
                <h3 className="font-display text-5xl font-bold bg-gradient-to-br from-primary-700 to-primary-900 bg-clip-text text-transparent mb-3">
                  {loading ? '…' : totalArtisansGlobal}
                </h3>
                <p className="text-gray-700 dark:text-gray-300 font-semibold text-lg">Artisans Talentueux</p>
                <div className="mt-4 pt-4 border-t border-primary-100">
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">Créateurs passionnés</p>
                </div>
              </div>
            </div>

            {/* Card Produits */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-accent-400 to-accent-600 rounded-3xl transform -rotate-3 group-hover:-rotate-6 transition-transform duration-300 opacity-20"></div>
              <div className="relative text-center p-5 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl border-2 border-accent-200 shadow-lg hover:shadow-2xl hover:-translate-y-3 transition-all duration-300">
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-accent-500 to-accent-700 rounded-2xl flex items-center justify-center mx-auto transform group-hover:rotate-12 group-hover:scale-110 transition-all duration-300 shadow-xl">
                    <i className="fas fa-box text-white text-3xl"></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-primary-400 rounded-full animate-ping opacity-30"></div>
                </div>
                <h3 className="font-display text-5xl font-bold bg-gradient-to-br from-accent-700 to-accent-900 bg-clip-text text-transparent mb-3">
                  {loading ? '…' : totalProduitsGlobal}
                </h3>
                <p className="text-gray-700 dark:text-gray-300 font-semibold text-lg">Œuvres Uniques</p>
                <div className="mt-4 pt-4 border-t border-accent-100">
                  <p className="text-sm dark:text-gray-400 text-gray-500 italic">Faits à la main</p>
                </div>
              </div>
            </div>

            {/* Card Régions */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-3xl transform rotate-2 group-hover:rotate-5 transition-transform duration-300 opacity-20"></div>
              <div className="relative text-center p-10 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl border-2 border-secondary-200 shadow-lg hover:shadow-2xl hover:-translate-y-3 transition-all duration-300">
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-secondary-500 to-secondary-700 rounded-2xl flex items-center justify-center mx-auto transform group-hover:rotate-12 group-hover:scale-110 transition-all duration-300 shadow-xl">
                    <i className="fas fa-map-marker-alt text-white text-3xl"></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-accent-400 rounded-full animate-ping opacity-30"></div>
                </div>
                <h3 className="font-display text-5xl font-bold bg-gradient-to-br from-secondary-700 to-secondary-900 bg-clip-text text-transparent mb-3">
                  14
                </h3>
                <p className="text-gray-700 font-semibold dark:text-gray-300 text-lg">Régions du Sénégal</p>
                <div className="mt-4 pt-4 border-t border-secondary-100">
                  <p className="text-sm dark:text-gray-400 text-gray-500 italic">Patrimoine national</p>
                </div>
              </div>
            </div>

            {/* Card Clients */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-red-400 to-red-600 dark:from-red-600 dark:to-red-800 rounded-3xl transform -rotate-2 group-hover:-rotate-5 transition-transform duration-300 opacity-20"></div>
              <div className="relative text-center p-10 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl border-2 border-red-200 dark:border-red-700 shadow-lg hover:shadow-2xl hover:-translate-y-3 transition-all duration-300">
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-red-700 dark:from-red-600 dark:to-red-800 rounded-2xl flex items-center justify-center mx-auto transform group-hover:rotate-12 group-hover:scale-110 transition-all duration-300 shadow-xl">
                    <i className="fas fa-heart text-white text-3xl"></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-accent-400 dark:bg-accent-500 rounded-full animate-ping opacity-30"></div>
                </div>
                <h3 className="font-display text-5xl font-bold bg-gradient-to-br from-red-700 to-red-900 dark:from-red-400 dark:to-red-600 bg-clip-text text-transparent mb-3">
                  750+
                </h3>
                <p className="text-gray-700 dark:text-gray-300 font-semibold text-lg">Clients Satisfaits</p>
                <div className="mt-4 pt-4 border-t border-red-100 dark:border-red-800">
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">Communauté fidèle</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ligne décorative ondulée en bas */}
        <div className="absolute bottom-0 left-0 right-0 h-24 opacity-10">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-full">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="currentColor" className="text-primary-200"></path>
          </svg>
        </div>
      </section>

     
    </div>
  );
};

export default Accueil;
